<template>
  <div class="wrapper">

    <!-- Sidebar -->

    <!-- Main Content -->
    <div class="main visual-2 mng-appoits">
      <Header :backRoute="'/appointments'" />

      <div class="manage-appointments-container">
        <!-- Page Header -->

        <!-- Filters Section -->
        <div class="filters-section">
          <div class="filter-row">

            <div class="filter-group">
              <label>Search Patient</label>
              <div class="search-box">
                <input type="text" v-model="filters.search" class="filter-input search-input"
                  placeholder="Search by patient name..." />
                <i class="fa-solid fa-search search-icon"></i>
              </div>
            </div>

            <div class="filter-group">
              <label>Doctor</label>
              <select v-model="filters.doctor" class="filter-select">
                <option value="">All Doctors</option>
                <option v-for="doctor in doctors" :key="doctor.id" :value="doctor.id">
                  {{ formatFullName(doctor) }}
                </option>
              </select>
            </div>

            <div class="filter-group">
              <label>Time Period</label>
              <select v-model="filters.timePeriod" class="filter-select">
                <option value="all">All Dates</option>
                <option value="today">Today</option>
                <option value="thisWeek">This Week</option>
                <option value="thisMonth">This Month</option>
                <option value="lastMonth">Last Month</option>
                <option value="custom">Custom Date Range</option>
              </select>
            </div>



            <!-- Custom Date Range -->
            <div v-if="filters.timePeriod === 'custom'" class="filter-group">
              <label>From Date</label>
              <input type="date" v-model="filters.customStartDate" class="filter-input" />
            </div>

            <div v-if="filters.timePeriod === 'custom'" class="filter-group">
              <label>To Date</label>
              <input type="date" v-model="filters.customEndDate" class="filter-input" />
            </div>

            <div class="filter-group">
              <label>Status</label>
              <select v-model="filters.status" class="filter-select">
                <option value="all">All Status</option>
                <option value="approved">Approved</option>
                <option value="cancelled">Cancelled</option>
                <option value="completed">Completed</option>
                <option value="open">Pending</option>
              </select>
            </div>

            <div class="filter-group">
              <label>Entrance Type</label>
              <select v-model="filters.entranceType" class="filter-select">
                <option value="all">All Entrances</option>
                <option value="withAppointment">With Appointment</option>
              </select>
            </div>

            <div class="filter-actions">
              <button @click="clearFilters" class="btn btn-secondary">
                Clear All
              </button>
            </div>

            <div class="filter-actions filter-actions-right">
              <router-link to="/appointment-dashboard" class="btn btn-outline filter-nav-btn">
                <i class="fa-solid fa-chart-line"></i> Dashboard Analytics
              </router-link>
            </div>

          </div>
        </div>






        <!-- Loader -->
        <div v-if="loading" class="loader">Loading appointments...</div>

        <!-- Appointments Table -->
        <div v-else class="appointments-table-section" style="padding-left: 20px;">
          <div class="table-header">
            <div class="header-left">
              <h3>Appointments</h3>
              <div class="items-per-page">
                <label>Per Page:</label>
                <select :value="itemsPerPage" @change="changeItemsPerPage(Number($event.target.value))"
                  class="items-select">
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="15">15</option>
                  <option value="20">20</option>
                </select>
              </div>
            </div>
            <div class="table-actions">
              <button @click="exportAppointments" class="btn btn-outline">
                Export
              </button>
            </div>
          </div>

          <div class="queue-table-container">
            <div class="queue-table-wrapper">
              <table class="queue-table">
                <thead>
                  <tr>
                    <th>Patient Name</th>
                    <th>Date</th>
                    <th>Doctor</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="appointment in filteredAppointments" :key="appointment.id">
                    <td>
                      <div class="patient-info">
                        <strong>{{ formatFullName(appointment.patient) }}</strong>
                      </div>
                    </td>
                    <td>{{ formatDate(appointment) }}</td>
                    <td>{{ formatDoctorName(appointment.doctor) }}</td>
                    <td>
                      <span :class="['status-badge', statusClass(appointment.status)]">
                        {{ formatStatus(appointment.status) }}
                      </span>
                    </td>
                    <td>
                      <div class="action-buttons">
                        <button class="btn btn-sm btn-primary" @click="openEditModal(appointment)"
                          title="View Appointment">
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div v-if="filteredAppointments.length === 0" class="no-appointments">
                <div class="no-data-icon">📅</div>
                <h4>No appointments found</h4>
                <p>Try adjusting your filters to see more results</p>
              </div>
            </div>
          </div>

          <!-- Pagination Controls -->
          <div v-if="totalPages > 0" class="pagination-section">
            <div class="pagination-info">
              <p>
                Showing
                <strong>{{ totalItems > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0 }}</strong> to
                <strong>{{ Math.min(currentPage * itemsPerPage, totalItems) }}</strong>
                of <strong>{{ totalItems }}</strong> appointments
              </p>
            </div>

            <div class="pagination-controls">
              <button class="pagination-btn" @click="previousPage" :disabled="currentPage === 1">
                ← Previous
              </button>

              <div class="pagination-numbers">
                <button v-for="page in getPageNumbers()" :key="page" :class="[
                  'pagination-number',
                  { active: currentPage === page },
                ]" @click="goToPage(page)">
                  {{ page }}
                </button>
              </div>

              <button class="pagination-btn" @click="nextPage" :disabled="currentPage === totalPages">
                Next →
              </button>
            </div>
          </div>
        </div>
      </div>

      <FooterView />
    </div>
  </div>

  <!-- View Appointment Modal -->
  <div v-if="showEditModal" class="modal modalDesign">
    <div class="modal-content edit-modal-content">
      <button class="close-btn" @click="closeEditModal">&times;</button>
      <h3 class="modal-title">View Appointment</h3>

      <div class="modal-body">
        <!-- SKELETON LOADER -->
        <div v-if="modalLoading" class="modal-skeleton">
          <div class="skeleton-patient-card">
            <div class="skeleton-line w-60"></div>
            <div class="skeleton-line w-40"></div>
            <div style="margin-top: 16px">
              <div class="skeleton-line w-80"></div>
              <div class="skeleton-line w-70" style="margin-top: 8px"></div>
            </div>
          </div>

          <div class="skeleton-group" style="margin-top: 24px">
            <div class="skeleton-input"></div>
            <div class="skeleton-input"></div>
          </div>

          <div class="skeleton-group">
            <div class="skeleton-input"></div>
            <div class="skeleton-input"></div>
          </div>

          <div class="skeleton-input" style="margin-top: 16px"></div>
        </div>

        <!-- ACTUAL FORM -->
        <div v-else class="edit-form-content">
          <!-- Patient Info Card -->
          <div class="patient-card">
            <div class="patient-card-header">
              <div class="patient-avatar">👤</div>
              <div class="patient-details">
                <h4 class="patient-name">
                  {{ selectedAppointment ? formatFullName(selectedAppointment.patient) : "" }}
                </h4>
                <div class="patient-meta">
                  <div class="patient-meta-item appointment-code">
                    <span class="patient-meta-label">Appointment Code</span>
                    <span class="patient-meta-value">
                      {{ selectedAppointment ? (selectedAppointment.appointmentCode ||
                        selectedAppointment.id) : "N/A" }}
                    </span>
                  </div>
                  <div class="patient-meta-item">
                    <span class="patient-meta-label">Email</span>
                    <span class="patient-meta-value">{{ getPatientEmail(selectedAppointment?.patient) }}</span>
                  </div>
                  <div class="patient-meta-item">
                    <span class="patient-meta-label">Phone</span>
                    <span class="patient-meta-value">{{ getPatientPhone(selectedAppointment?.patient) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Status and Doctor Selection -->
          <div class="form-section">
            <div class="form-row">
              <div class="form-group statuss">
                <label class="form-label">Status</label>
                <input type="text" :value="formatStatus(editForm.status)" class="form-control" readonly />
              </div>

              <div class="form-group doctorss">
                <label class="form-label">Doctor</label>
                <input type="text" :value="formatDoctorName(selectedAppointment?.doctor)" class="form-control"
                  readonly />
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Appointment Type</label>
                <input type="text" :value="getAppointmentTypeName(editForm.appointmentTypeCode)" class="form-control"
                  readonly />
              </div>
            </div>
          </div>

          <!-- Calendar and Time Slots Section -->
          <div class="form-section timeslots-section" style="margin-top: 0px;">
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Date</label>
                <input type="text" :value="formatDate(selectedAppointment)" class="form-control" readonly />
              </div>

              <div class="form-group">
                <label class="form-label">Start Time</label>
                <input type="text" :value="formatTime(selectedAppointment)" class="form-control" readonly />
              </div>

              <div class="form-group">
                <label class="form-label">End Time</label>
                <input type="text" :value="formatEndTime(selectedAppointment)" class="form-control" readonly />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="modal-footer">
        <button class="btn btn-outline" @click="openPurposeModal(selectedAppointment)"
          :disabled="modalLoading || downloadingId === selectedAppointment?.id" title="Download Intake Form PDF">
          {{ downloadingId === selectedAppointment?.id ? "Downloading..." : "Download Intake Form" }}
        </button>
        <button class="btn btn-secondary" @click="closeEditModal" :disabled="isLoading">
          Close
        </button>
      </div>
    </div>
  </div>

  <!-- Download Purpose Modal -->
  <div v-if="showPurposeModal" class="modal modalDesign">
    <div class="modal-content">
      <button class="close-btn" @click="closePurposeModal">&times;</button>
      <h3 class="modal-title">Download Intake Form</h3>

      <div class="modal-body">
        <div class="form-group">
          <label class="form-label">Purpose *</label>
          <input type="text" v-model="purposeInput" class="form-control" placeholder="e.g. Insurance claim, patient record request"
            @keyup.enter="confirmDownload" />
          <p v-if="purposeError" class="form-error-banner" style="margin-top: 8px;">
            {{ purposeError }}
          </p>
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn btn-secondary" @click="closePurposeModal" :disabled="downloadingId">
          Cancel
        </button>
        <button class="btn btn-primary" @click="confirmDownload" :disabled="!!downloadingId">
          {{ downloadingId ? "Downloading..." : "Download" }}
        </button>
      </div>
    </div>
  </div>

  <!-- Off-screen render target for the intake form PDF snapshot. Always
       mounted (never v-if) so its ref is available the instant a download
       is triggered; kept out of the visible layout via absolute + negative
       offset rather than display:none, since html2canvas can't capture
       elements that aren't actually laid out. -->
  <div class="intake-print-offscreen">
    <IntakeFormPrintTemplate
      ref="intakePrintTemplate"
      :form-data="printFormData || {}"
      :submitted-at="printSubmittedAt"
    />
  </div>
</template>

<script>
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { debounce } from "lodash";
import html2pdf from "html2pdf.js";

import FooterView from "@/layout/Footer.vue";
import Header from "@/layout/Header.vue";
import { AppointmentService } from "@/services/appointments.service";
import { DoctorsService } from "@/services/doctors.service";
import { AppointmentTypeService } from "@/services/appointmentType.service";
import { DoctorTimeSlotService } from "@/services/doctor-time-slots.service";
import { AnalyticsService } from "@/services/analytics.service";
import { IntakeFormService } from "@/services/intakeForm.service";
import IntakeFormPrintTemplate from "@/components/appointments/IntakeFormPrintTemplate.vue";

export default {
  components: {
    Header,
    FooterView,
    IntakeFormPrintTemplate,
  },
  name: "ManageAppointments",
  data() {
    return {
      loading: false,
      isLoading: false,
      showEditModal: false,
      refreshInterval: null,
      loadingDoctors: false,
      doctorAvailability: [],
      selectedDoctorAvailability: null,
      availabilityLoading: false,
      availableTimeSlots: [],
      availableTimeSlots24hr: [],
      availableSlotsRaw: [],
      modalLoading: false,
      downloadingId: null,
      showPurposeModal: false,
      purposeInput: "",
      purposeError: "",
      pendingDownloadAppointment: null,
      printFormData: null,
      printSubmittedAt: "",
      flatpickrInstance: null,
      appointmentReasons: [],
      enabledDays: [],

      // Filters
      filters: {
        search: "",
        doctor: "",
        timePeriod: "all",
        status: "all",
        entranceType: "all",
        customStartDate: "",
        customEndDate: "",
      },

      analyticsPeriod: "monthly",

      // Data
      appointments: [],
      doctors: [],

      // Server-side Pagination
      currentPage: 1,
      itemsPerPage: 10,
      totalItems: 0,
      totalPages: 1,
      hasNextPage: false,
      hasPreviousPage: false,

      // Stats
      stats: {
        total: 0,
        pending: 0,
        approved: 0,
        cancelled: 0,
        completed: 0
      },

      // Selected appointment for editing
      selectedAppointment: null,
      editForm: {
        date: "",
        timeSlot: "",
        endTime: "",
        timeSlotId: "",
        doctorId: "",
        appointmentTypeCode: "",
        status: "",
        notes: "",
      },

      // Analytics Data
      analyticsData: {
        failed: null,
        missed: null,
        successful: null,
        patientsRegistered: null,
        bookingRatio: null,
      },
    };
  },
  computed: {
    filteredAppointments() {
      return this.appointments || [];
    },

    isFormValid() {
      return (
        this.editForm.date &&
        this.editForm.timeSlot &&
        this.editForm.doctorId &&
        this.editForm.appointmentTypeCode &&
        this.editForm.status
      );
    },

    // Build query params for API
    queryParams() {
      const params = {
        page: this.currentPage,
        limit: this.itemsPerPage,
      };

      if (this.filters.search && this.filters.search.trim()) {
        params.search = this.filters.search.trim();
      }

      if (this.filters.doctor) {
        params.doctorId = this.filters.doctor;
      }

      if (this.filters.status && this.filters.status !== 'all') {
        params.status = this.filters.status;
      }

      if (this.filters.timePeriod && this.filters.timePeriod !== 'all') {
        params.period = this.filters.timePeriod;

        if (this.filters.timePeriod === 'custom') {
          if (this.filters.customStartDate) {
            params.startDate = this.filters.customStartDate;
          }
          if (this.filters.customEndDate) {
            params.endDate = this.filters.customEndDate;
          }
        }
      }

      if (this.filters.entranceType) {
        params.entranceType = this.filters.entranceType;
      }

      return params;
    },
    analyticsStats() {
      if (this.analyticsData.failed && this.analyticsData.missed && this.analyticsData.successful) {
        return {
          failed: {
            value: this.analyticsData.failed.count,
            diff: "0%", // API doesn't provide diff for these yet
            trend: [0, 0, 0, 0, 0, this.analyticsData.failed.count],
            ...this.buildPaths([0, 0, 0, 0, 0, this.analyticsData.failed.count])
          },
          missed: {
            value: this.analyticsData.missed.count,
            diff: "0%",
            trend: [0, 0, 0, 0, 0, this.analyticsData.missed.count],
            ...this.buildPaths([0, 0, 0, 0, 0, this.analyticsData.missed.count])
          },
          successful: {
            value: this.analyticsData.successful.count,
            diff: "0%",
            trend: [0, 0, 0, 0, 0, this.analyticsData.successful.count],
            ...this.buildPaths([0, 0, 0, 0, 0, this.analyticsData.successful.count])
          },
          patientsRegistered: {
            value: this.analyticsData.patientsRegistered?.count || 0,
            diff: "0%",
            trend: [0, 0, 0, 0, 0, this.analyticsData.patientsRegistered?.count || 0],
            ...this.buildPaths([0, 0, 0, 0, 0, this.analyticsData.patientsRegistered?.count || 0])
          },
          bookingRatio: {
            value: this.analyticsData.bookingRatio?.change || "0%",
            trend: [0, 0, 0, 0, 0, parseFloat(this.analyticsData.bookingRatio?.currentMonth || 0)],
            ...this.buildPaths([0, 0, 0, 0, 0, parseFloat(this.analyticsData.bookingRatio?.currentMonth || 0)])
          },
        };
      }

      // Fallback to calculation from appointments if API data is not yet loaded
      const now = new Date();
      const isYearly = this.analyticsPeriod === "yearly";
      const isDaily = this.analyticsPeriod === "daily";

      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth();
      const currentDate = now.getDate();

      // Calculation Helper
      const getStatsForPeriod = (date, month, year) => {
        const apps = this.appointments?.filter((a) => {
          const d = new Date(a.assignedStartTime);
          if (date !== null) {
            return d.getDate() === date && d.getMonth() === month && d.getFullYear() === year;
          }
          if (month !== null) {
            return d.getMonth() === month && d.getFullYear() === year;
          }
          return d.getFullYear() === year;
        });

        const failed = apps.filter((a) => a.status === "cancelled").length;
        const missed = apps.filter((a) => {
          const appDate = new Date(a.assignedStartTime);
          return (a.status === "open" || a.status === "approved") && appDate < now;
        }).length;
        const successful = apps.filter((a) => a.status === "completed").length;
        const patients = new Set(apps.map((a) => a.patient?.id)).size;

        return { total: apps.length, failed, missed, successful, patients };
      };

      // Current Period Stats
      const current = isDaily
        ? getStatsForPeriod(currentDate, currentMonth, currentYear)
        : isYearly
          ? getStatsForPeriod(null, null, currentYear)
          : getStatsForPeriod(null, currentMonth, currentYear);

      // Previous Period Stats (for percentages)
      let prev;
      if (isDaily) {
        const yesterday = new Date(now);
        yesterday.setDate(now.getDate() - 1);
        prev = getStatsForPeriod(yesterday.getDate(), yesterday.getMonth(), yesterday.getFullYear());
      } else if (isYearly) {
        prev = getStatsForPeriod(null, null, currentYear - 1);
      } else {
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        prev = getStatsForPeriod(null, lastMonth.getMonth(), lastMonth.getFullYear());
      }

      // Trend Data Helper (Last 6 points)
      const getTrend = (type) => {
        const points = [];
        for (let i = 5; i >= 0; i--) {
          let m, y, d;
          if (isDaily) {
            const date = new Date(now);
            date.setDate(now.getDate() - i);
            d = date.getDate();
            m = date.getMonth();
            y = date.getFullYear();
          } else if (isYearly) {
            y = currentYear - i;
            m = null;
            d = null;
          } else {
            const date = new Date(currentYear, currentMonth - i, 1);
            m = date.getMonth();
            y = date.getFullYear();
            d = null;
          }
          const s = getStatsForPeriod(d, m, y);
          points.push(s[type] || 0);
        }
        return points;
      };

      const calculateDiff = (curr, previous) => {
        if (previous === 0) return curr > 0 ? "+100%" : "0%";
        const diff = ((curr - previous) / previous) * 100;
        return (diff >= 0 ? "+" : "") + diff.toFixed(1) + "%";
      };

      // Path Builder Helper
      const buildPaths = (trendData) => {
        const max = Math.max(...trendData) || 1;
        const pts = trendData.map((p, i) => ({
          x: i * 20,
          y: 28 - (p / max) * 25
        }));

        const linePoints = pts.map(p => `${p.x},${p.y}`).join(' ');
        const areaPath = `M 0,30 ` + pts.map(p => `L ${p.x},${p.y}`).join(' ') + ` L 100,30 Z`;

        return { linePoints, areaPath };
      };

      return {
        failed: {
          value: current.failed,
          diff: calculateDiff(current.failed, prev.failed),
          trend: getTrend('failed'),
          ...buildPaths(getTrend('failed'))
        },
        missed: {
          value: current.missed,
          diff: calculateDiff(current.missed, prev.missed),
          trend: getTrend('missed'),
          ...buildPaths(getTrend('missed'))
        },
        successful: {
          value: current.successful,
          diff: calculateDiff(current.successful, prev.successful),
          trend: getTrend('successful'),
          ...buildPaths(getTrend('successful'))
        },
        patientsRegistered: {
          value: current.patients,
          diff: calculateDiff(current.patients, prev.patients),
          trend: getTrend('patients'),
          ...buildPaths(getTrend('patients'))
        },
        bookingRatio: {
          value: calculateDiff(current.total, prev.total),
          trend: getTrend('total'),
          ...buildPaths(getTrend('total'))
        },
      };
    },
  },
  methods: {

    buildPaths(trendData) {
      const max = Math.max(...trendData) || 1;

      const pts = trendData.map((p, i) => ({
        x: i * 20,
        y: 28 - (p / max) * 25
      }));

      const linePoints = pts.map(p => `${p.x},${p.y}`).join(" ");
      const areaPath =
        "M 0,30 " + pts.map(p => `L ${p.x},${p.y}`).join(" ") + " L 100,30 Z";

      return { linePoints, areaPath };
    },
    // ==================== FORMATTING METHODS FOR NEW DATA STRUCTURE ====================
    convertAndAdjustTime(time, subtractHours = 6) {
      if (!time) return "";

      const [hours, minutes] = time.split(":").map(Number);

      const date = new Date();
      date.setHours(hours);
      date.setMinutes(minutes);
      date.setSeconds(0);

      date.setHours(date.getHours() - subtractHours);

      return `${String(date.getHours()).padStart(2, "0")}:${String(
        date.getMinutes()
      ).padStart(2, "0")}`;
    },
    formatDate(appointment) {
      if (!appointment) return "";

      // Use assignDate if available
      if (appointment.assignDate) {
        const normalizedDate = this.formatDateForInput(appointment.assignDate);
        if (!normalizedDate) return "";

        const [year, month, day] = normalizedDate.split("-");
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      }

      // Fallback to assignedStartTime if assignDate not available
      if (appointment.assignedStartTime) {
        const date = new Date(appointment.assignedStartTime);
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      }

      return "";
    },

    formatTime(appointment) {
      if (!appointment) return "";

      return this.formatTimeDisplay(appointment.assignedStartTime);
    },

    formatEndTime(appointment) {
      if (!appointment) return "";

      return this.formatTimeDisplay(appointment.assignedEndTime);
    },

    formatAppointmentTimeRange(appointment) {
      if (!appointment) return "";

      const start = this.formatTime(appointment);
      // const end = this.formatEndTime(appointment);

      if (start) {
        return `${start}`;
      }

      return start || "";
    },

    formatTimeDisplay(time) {
      if (!time) return "";

      const localTime = this.convertToLocalTime(null, time);
      if (!localTime) return "";

      const [hours, minutes] = localTime.split(":").map(Number);
      const localDate = new Date();
      localDate.setHours(hours, minutes, 0, 0);

      return localDate.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    },

    formatDoctorName(doctor) {
      if (!doctor) return "Unknown";

      // Handle case where doctor has empty firstname/lastname
      if (doctor.firstname === "" && doctor.lastname === "") {
        return "Doctor";
      }

      return this.formatFullName(doctor);
    },

    // ==================== FILTER METHODS ====================

    applyFiltersDebounced: debounce(function () {
      this.currentPage = 1;
      this.getAppointments();
    }, 500),

    clearFilters() {
      this.filters = {
        search: "",
        doctor: "",
        timePeriod: "all",
        status: "all",
        entranceType: "all",
        customStartDate: "",
        customEndDate: "",
      };
      this.currentPage = 1;
      this.getAppointments();
    },

    // ==================== API METHODS ====================

    async getAppointments(silent = false) {
      if (!silent) this.loading = true;

      try {
        const res = await AppointmentService.getList(this.queryParams);

        if (res.success) {
          // Handle different response structures
          this.appointments = res.data?.appointments || res.data?.data || res.data || [];

          // Handle pagination data from response
          if (res.data?.pagination) {
            this.totalItems = res.data.pagination.totalCount || res.data.pagination.totalItems || 0;
            this.totalPages = res.data.pagination.totalPages || 1;
            this.currentPage = res.data.pagination.currentPage || this.currentPage;
            this.hasNextPage = this.currentPage < this.totalPages;
            this.hasPreviousPage = this.currentPage > 1;
          } else if (res.pagination) {
            this.totalItems = res.pagination.totalCount || res.pagination.totalItems || 0;
            this.totalPages = res.pagination.totalPages || 1;
            this.currentPage = res.pagination.currentPage || this.currentPage;
            this.hasNextPage = this.currentPage < this.totalPages;
            this.hasPreviousPage = this.currentPage > 1;
          }

          // Update stats from API counts so cards reflect the full result set,
          // not just the appointments loaded for the current page.
          this.updateStats(res.data?.counts);
        } else {
          this.$store.commit("toast/NEW", {
            type: "error",
            message: res.message || "Failed to load appointments",
          });
        }
      } catch (error) {
        console.error("Error fetching appointments:", error);
        this.$store.commit("toast/NEW", {
          type: "error",
          message: "Failed to load appointments. Please try again.",
        });
      } finally {
        if (!silent) this.loading = false;
      }
    },

    updateStats(apiCounts = null) {
      if (apiCounts) {
        this.stats = {
          total: apiCounts.totalCount || 0,
          pending: apiCounts.pendingCount || 0,
          approved: apiCounts.approvedCount || 0,
          cancelled: apiCounts.cancelledCount || 0,
          completed: apiCounts.completedCount || 0,
        };
        return;
      }

      if (!this.appointments || !this.appointments.length) {
        this.stats = {
          total: this.totalItems,
          pending: 0,
          approved: 0,
          cancelled: 0,
          completed: 0,
        };
        return;
      }

      const pending = this.appointments.filter(a => a.status === "open").length;
      const approved = this.appointments.filter(a => a.status === "approved").length;
      const cancelled = this.appointments.filter(a => a.status === "cancelled").length;
      const completed = this.appointments.filter(a => a.status === "completed").length;

      this.stats = {
        total: this.totalItems,
        pending,
        approved,
        cancelled,
        completed,
      };
    },

    async getDoctors() {
      this.loadingDoctors = true;

      try {
        const hospitalId = this.$store.state.user.currentUser?.objectId ||
          this.$store.state.user.currentUser?.hospitalId;
        const res = await DoctorsService.getSyncDoctors({ hospitalId });

        if (res.success) {
          this.doctors = res.data.doctors || res.data || [];
        } else {
          this.$store.commit("toast/NEW", {
            type: "error",
            message: res.message || "Failed to load doctors",
          });
        }
      } catch (error) {
        console.error("Error fetching doctors:", error);
        this.$store.commit("toast/NEW", {
          type: "error",
          message: "Failed to load doctors. Please try again.",
        });
      } finally {
        this.loadingDoctors = false;
      }
    },

    async fetchAppointmentReasons() {
      try {
        const hospitalId = this.$store.state.user.currentUser?.objectId;
        if (!hospitalId) return;

        const response = await AppointmentTypeService.getAll({ hospitalId });
        const data = response?.data?.data;

        if (data) {
          this.appointmentReasons = (data.data || [])
            .map(t => ({
              value: t?.id || t?.code || "",
              label: t?.label || "",
              description: t?.description || "",
              name: t?.name || "",
              code: t?.code || "",
            }))
            .filter(i => i.value && (i.label || i.description || i.name));
        }
      } catch (error) {
        console.error("Error fetching appointment types:", error);
      }
    },

    async fetchDoctorAvailability() {
      if (!this.selectedAppointment?.hospitalId && !this.selectedAppointment?.hospital?.id) {
        return;
      }

      this.availabilityLoading = true;

      try {
        const hospitalId = this.selectedAppointment.hospital?.id || this.selectedAppointment.hospitalId;

        // Fetch sitting dates for the selected doctor
        if (this.editForm.doctorId) {
          const resSitting = await DoctorTimeSlotService.getSittingDates({
            hospitalId,
            doctorId: this.editForm.doctorId,
          });

          const enabledDaysList = resSitting.data?.data || resSitting.data || [];
          if (resSitting.success && enabledDaysList.length > 0) {
            this.enabledDays = enabledDaysList[0]?.enabledDays || [];
          } else {
            this.enabledDays = [];
          }
        }
      } catch (err) {
        console.error("Doctor availability/sitting dates fetch failed", err);
        this.enabledDays = [];
      } finally {
        this.availabilityLoading = false;
      }
    },

    async buildTimeSlotsForDate(date) {
      if (!this.editForm.doctorId || !date || !this.editForm.appointmentTypeCode) {
        this.availableTimeSlots = [];
        this.availableTimeSlots24hr = [];
        this.availableSlotsRaw = [];
        return;
      }

      this.availabilityLoading = true;
      try {
        const res = await DoctorTimeSlotService.getTimeslotsV2({
          doctorId: this.editForm.doctorId,
          date: date,
          appointmentType: this.editForm.appointmentTypeCode,
        });

        const rawSlots = [];

        if (Array.isArray(res.data)) {
          res.data.forEach((doc) => {
            if (doc.slots) {
              doc.slots.forEach((slot) => {
                const time24 = slot.startTime;
                const time12 = this.convertTo12HourFormat(time24);
                rawSlots.push({
                  id: slot?.timeSlotId || slot?.id || slot?._id,
                  label: time12,
                  startTime: time24,
                  endTime: slot.endTime,
                });
              });
            }
          });
        }

        this.availableSlotsRaw = rawSlots;
        this.availableTimeSlots = rawSlots.map((s) => s.label);
        this.availableTimeSlots24hr = rawSlots.map((s) => s.startTime);
      } catch (err) {
        console.error("Fetch timeslots failed", err);
      } finally {
        this.availabilityLoading = false;
      }
    },

    async updateAppointment() {
      if (!this.isFormValid) {
        alert("Please fill all required fields");
        return;
      }

      this.isLoading = true;

      const payload = {
        doctorId: this.editForm.doctorId,
        status: this.editForm.status,
        assignDate: this.editForm.date,
        appointmentTypeCode: this.editForm.appointmentTypeCode,
        assignedStartTime: this.editForm.timeSlot,
        assignedEndTime: this.editForm.endTime,
        timeSlotId: this.editForm.timeSlotId,
      };

      try {
        const res = await AppointmentService.updateAppointment(
          this.selectedAppointment.id,
          payload
        );

        if (res.data?.data || res.success) {
          this.$store.commit("toast/NEW", {
            type: "success",
            message: "Appointment updated successfully",
          });
          this.showEditModal = false;
          this.getAppointments();
        } else {
          this.$store.commit("toast/NEW", {
            type: "error",
            message: res.message || "Failed to update appointment",
          });
        }
      } catch (error) {
        console.error("Error updating appointment:", error);
        this.$store.commit("toast/NEW", {
          type: "error",
          message: "Failed to update appointment. Please try again.",
        });
      } finally {
        this.isLoading = false;
      }
    },

    // ==================== ANALYTICS METHODS ====================

    async fetchAnalytics() {
      const now = new Date();
      let period;
      let filter;

      if (this.analyticsPeriod === "daily") {
        period = now.toISOString().split("T")[0];
        filter = "day";
      } else if (this.analyticsPeriod === "monthly") {
        period = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
        filter = "month";
      } else {
        period = `${now.getFullYear()}`;
        filter = "year";
      }

      const params = { period, filter };

      try {
        const [failed, missed, successful, registered, ratio] = await Promise.all([
          AnalyticsService.getFailedAppointments(params),
          AnalyticsService.getMissedAppointments(params),
          AnalyticsService.getSuccessfulAppointments(params),
          AnalyticsService.getPatientsRegistered(params),
          AnalyticsService.getBookingRatio(params),
        ]);

        this.analyticsData = {
          failed: failed.data,
          missed: missed.data,
          successful: successful.data,
          patientsRegistered: registered.data,
          bookingRatio: ratio.data,
        };
      } catch (error) {
        console.error("Error fetching analytics:", error);
      }
    },

    // ==================== UI METHODS ====================

    initializeFlatpickr() {
      if (this.flatpickrInstance) {
        this.flatpickrInstance.destroy();
      }

      this.$nextTick(() => {
        if (!this.$refs.calendarInput) return;

        this.flatpickrInstance = flatpickr(this.$refs.calendarInput, {
          mode: "single",
          minDate: "today",
          enable: [
            (date) => {
              if (this.enabledDays.length === 0) return true;
              const dayName = date.toLocaleDateString("en-US", {
                weekday: "long",
              });
              return this.enabledDays.includes(dayName);
            },
          ],
          dateFormat: "Y-m-d",
          onChange: (selectedDates) => {
            if (selectedDates.length > 0) {
              this.editForm.date = selectedDates[0].toISOString().split("T")[0];
              this.buildTimeSlotsForDate(this.editForm.date);
            }
          },
        });

        if (this.editForm.date) {
          this.flatpickrInstance.setDate(this.editForm.date);
        }
      });
    },

    selectSlot(slot) {
      this.editForm.timeSlot = slot.startTime;
      this.editForm.endTime = slot.endTime;
      this.editForm.timeSlotId = slot.id;
    },

    // async openEditModal(appointment) {
    //   this.showEditModal = true;
    //   this.modalLoading = true;

    //   this.selectedAppointment = appointment;

    //   // Handle date from assignDate or assignedStartTime
    //   let dateValue = "";
    //   if (appointment.assignDate) {
    //     dateValue = appointment.assignDate;
    //   } else if (appointment.assignedStartTime) {
    //     dateValue = this.formatDateForInput(appointment.assignedStartTime);
    //   }

    //   // Handle time from assignedStartTime
    //   let timeSlotValue = "";
    //   if (appointment.assignedStartTime) {
    //     if (!appointment.assignedStartTime.includes('T')) {
    //       timeSlotValue = this.convertAndAdjustTime(appointment.assignedStartTime);
    //     } else {
    //       // const d = new Date(appointment.assignedStartTime);
    //       // timeSlotValue = `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
    //       timeSlotValue = this.convertToLocalTime(
    //         appointment.assignDate,
    //         appointment.assignedStartTime
    //       );
    //     }
    //   }

    //   let endTimeValue = "";
    //   if (appointment.assignedEndTime) {
    //     if (!appointment.assignedEndTime.includes('T')) {
    //       endTimeValue = this.convertAndAdjustTime(appointment.assignedEndTime);
    //     } else {
    //       const d = new Date(appointment.assignedEndTime);
    //       endTimeValue = `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
    //     }
    //   }

    //   this.editForm = {
    //     date: dateValue,
    //     timeSlot: timeSlotValue,
    //     endTime: endTimeValue,
    //     timeSlotId: appointment.timeSlotId || "",
    //     doctorId: appointment.doctor?.id || "",
    //     appointmentType: appointment.appointmentType || appointment.appointmentTypeCode || "",
    //     status: appointment.status,
    //     notes: appointment.notes || "",
    //   };

    //   await this.fetchAppointmentReasons();

    //   // Match appointment type by code
    //   if (appointment.appointmentTypeCode && this.appointmentReasons.length > 0) {
    //     const foundType = this.appointmentReasons.find(
    //       (r) =>
    //         r.code === appointment.appointmentTypeCode ||
    //         r.label === appointment.appointmentTypeCode ||
    //         r.description === appointment.appointmentTypeCode
    //     );
    //     if (foundType) {
    //       this.editForm.appointmentTypeCode = foundType.value;
    //     }
    //   }

    //   await this.fetchDoctorAvailability();

    //   this.modalLoading = false;

    //   this.$nextTick(() => {
    //     this.initializeFlatpickr();
    //     if (this.editForm.date) {
    //       this.buildTimeSlotsForDate(this.editForm.date);
    //     }
    //   });
    // },
    async openEditModal(appointment) {
      this.showEditModal = true;
      this.modalLoading = true;

      this.selectedAppointment = appointment;

      // Handle date from assignDate or assignedStartTime
      let dateValue = "";
      if (appointment.assignDate) {
        dateValue = this.formatDateForInput(appointment.assignDate);
      } else if (appointment.assignedStartTime) {
        dateValue = this.formatDateForInput(appointment.assignedStartTime);
      }

      // ✅ Always convert using UTC - 6 rule
      const timeSlotValue = this.convertToLocalTime(
        appointment.assignDate,
        appointment.assignedStartTime
      );

      const endTimeValue = this.convertToLocalTime(
        appointment.assignDate,
        appointment.assignedEndTime
      );

      this.editForm = {
        date: dateValue,
        timeSlot: timeSlotValue,
        endTime: endTimeValue,
        timeSlotId: appointment.timeSlotId || "",
        doctorId: appointment.doctor?.id || "",
        // appointmentTypeCode: appointment.appointmentTypeCode || appointment.appointmentType || appointment.appointmentCode || "",
        appointmentTypeCode: appointment.appointmentTypeCode || appointment.appointmentCode || "",
        status: appointment.status,
        notes: appointment.notes || "",
      };

      await this.fetchAppointmentReasons();

      // Match appointment type by code
      if (appointment.appointmentTypeCode && this.appointmentReasons.length > 0) {
        const foundType = this.appointmentReasons.find(
          (r) =>
            r.code === appointment.appointmentTypeCode ||
            r.label === appointment.appointmentTypeCode ||
            r.description === appointment.appointmentTypeCode
        );

        console.log("foundType", foundType);
        if (foundType) {
          this.editForm.appointmentTypeCode = foundType.value;
        }
      }

      await this.fetchDoctorAvailability();

      this.modalLoading = false;

      this.$nextTick(() => {
        this.initializeFlatpickr();
        if (this.editForm.date) {
          this.buildTimeSlotsForDate(this.editForm.date);
        }
      });
    },
    convertToLocalTime(date, time) {
      if (!time) return "";

      let hours, minutes;

      // CASE 1: ISO datetime
      if (time.includes("T")) {
        const d = new Date(time);
        hours = d.getUTCHours();
        minutes = d.getUTCMinutes();
      }
      // CASE 2: HH:mm format
      else {
        [hours, minutes] = time.split(":").map(Number);
      }

      // subtract 6 hours
      hours = hours - 6;

      if (hours < 0) {
        hours = hours + 24;
      }

      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    },

    // convertToLocalTime(date, time) {
    //   if (!time) return "";

    //   let d;

    //   // ISO datetime
    //   if (time.includes("T")) {
    //     d = new Date(time);
    //   }
    //   // HH:mm format from API
    //   else {
    //     d = new Date(`${date}T${time}:00Z`);
    //   }

    //   return new Intl.DateTimeFormat("en-US", {
    //     timeZone: "America/Chicago",
    //     hour: "2-digit",
    //     minute: "2-digit",
    //     hour12: true,
    //   }).format(d);
    // },
    closeEditModal() {
      this.showEditModal = false;
      this.selectedAppointment = null;
      this.availableTimeSlots = [];
      this.availableTimeSlots24hr = [];
      this.availableSlotsRaw = [];
      this.selectedDoctorAvailability = null;
      this.doctorAvailability = [];
      this.editForm = {
        date: "",
        timeSlot: "",
        endTime: "",
        timeSlotId: "",
        doctorId: "",
        appointmentType: "",
        status: "",
        notes: "",
      };

      if (this.flatpickrInstance) {
        this.flatpickrInstance.destroy();
        this.flatpickrInstance = null;
      }
    },

    // ==================== UTILITY METHODS ====================

    formatFullName(person, limit = 20) {
      if (!person) return "N/A";
      let first = person.firstName || person.firstname || "";
      let last = person.lastName || person.lastname || "";
      let fullName = this.toTitleCase(`${first} ${last}`.trim());

      if (!fullName) return "Unknown";

      if (fullName.length > limit) {
        fullName = fullName.substring(0, limit).trim() + "...";
      }
      return fullName;
    },

    getPatientEmail(patient) {
      return patient?.email ? String(patient.email).toLowerCase() : "N/A";
    },

    getPatientPhone(patient) {
      return patient?.phoneNumber || patient?.phone || patient?.contactNumber || "N/A";
    },

    toTitleCase(value) {
      if (!value) return "";

      return String(value)
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());
    },

    formatDateForInput(dateString) {
      if (!dateString) return "";

      // If it's already YYYY-MM-DD format
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
      }

      // Normalize YYYY/MM/DD format from API
      if (/^\d{4}\/\d{2}\/\d{2}$/.test(dateString)) {
        return dateString.replace(/\//g, "-");
      }

      const s = String(dateString);

      if (/T.*Z$/.test(s) || /[+-]\d{2}:\d{2}$/.test(s)) {
        const parsed = new Date(s);
        if (isNaN(parsed)) return "";
        return `${parsed.getUTCFullYear()}-${String(parsed.getUTCMonth() + 1).padStart(2, "0")}-${String(parsed.getUTCDate()).padStart(2, "0")}`;
      }

      const d = new Date(s);
      if (isNaN(d)) return "";
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    },

    convertTo12HourFormat(time24) {
      if (!time24) return "";
      const [hours, minutes] = time24.split(":").map(Number);
      const period = hours >= 12 ? "PM" : "AM";
      const hours12 = hours % 12 || 12;
      return `${hours12}:${minutes.toString().padStart(2, "0")} ${period}`;
    },

    getAppointmentTypeName(typeValue) {
      if (!typeValue) return "";
      if (this.appointmentReasons && this.appointmentReasons.length > 0) {
        const type = this.appointmentReasons.find(r => r.value === typeValue);
        return type ? (type.description || type.label || type.name) : typeValue;
      }
      return typeValue;
    },

    formatStatus(status) {
      const statusMap = {
        open: "Pending",
        approved: "Approved",
        cancelled: "Cancelled",
        completed: "Completed",
        started: "Checked In",
        inprogress: "In Progress",
        closed: "Closed",
      };
      return statusMap[status] || status;
    },

    statusClass(status) {
      const map = {
        open: "status-pending",
        approved: "status-approved",
        cancelled: "status-cancelled",
        completed: "status-completed",
        started: "status-started",
        inprogress: "status-inprogress",
        closed: "status-closed",
      };
      return map[status] || "status-default";
    },

    // ==================== PAGINATION METHODS ====================

    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.getAppointments();
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage += 1;
        this.getAppointments();
      }
    },

    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage -= 1;
        this.getAppointments();
      }
    },

    changeItemsPerPage(newItemsPerPage) {
      this.itemsPerPage = newItemsPerPage;
      this.currentPage = 1;
      this.getAppointments();
    },

    getPageNumbers() {
      const pages = [];
      const maxVisiblePages = 5;
      let startPage = Math.max(1, this.currentPage - Math.floor(maxVisiblePages / 2));
      let endPage = Math.min(this.totalPages, startPage + maxVisiblePages - 1);

      if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      return pages;
    },

    // ==================== INTAKE FORM DOWNLOAD ====================

    openPurposeModal(appointment) {
      if (!appointment?.id) return;
      this.pendingDownloadAppointment = appointment;
      this.purposeInput = "";
      this.purposeError = "";
      this.showPurposeModal = true;
    },

    closePurposeModal() {
      if (this.downloadingId) return;
      this.showPurposeModal = false;
      this.pendingDownloadAppointment = null;
      this.purposeInput = "";
      this.purposeError = "";
    },

    confirmDownload() {
      if (!this.purposeInput.trim()) {
        this.purposeError = "Please enter a purpose for this download.";
        return;
      }

      const appointment = this.pendingDownloadAppointment;
      const purpose = this.purposeInput.trim();
      this.showPurposeModal = false;
      this.downloadIntakeForm(appointment, purpose);
    },

    async downloadIntakeForm(appointment, purpose) {
      if (!appointment?.id || this.downloadingId) return;

      this.downloadingId = appointment.id;

      try {
        let intakeForm = null;
        try {
          const res = await IntakeFormService.download({
            appointmentId: appointment.id,
            purpose,
          });
          intakeForm = res?.data || null;
        } catch (fetchError) {
          // No submitted intake form for this appointment (e.g. 404) — fall
          // through and render a blank form rather than blocking the download,
          // per the requirement that a missing/empty form still produces a PDF.
          console.warn("No intake form data returned, rendering a blank form:", fetchError);
        }

        // Feed the fetched (or empty) data into the off-screen template and
        // wait for Vue to render it before snapshotting.
        this.printFormData = intakeForm?.formData || {};
        this.printSubmittedAt = intakeForm?.submittedAt || "";
        await this.$nextTick();

        const templateEl = this.$refs.intakePrintTemplate?.$el;
        if (!templateEl) {
          throw new Error("Could not render the intake form template.");
        }

        const fileName = `intake-form-${this.formatFullName(appointment.patient) || "patient"
          }.pdf`.replace(/\s+/g, "-");

        // "avoid-all"/"css" pagebreak modes stop a page break from landing
        // in the middle of a field box, option row, or paragraph — the
        // element is pushed whole onto the next page instead of being
        // sliced through (which was producing cut-off rows and duplicated
        // overlapping text with the previous plain html2canvas+jsPDF slicing).
        await html2pdf()
          .set({
            filename: fileName,
            margin: 0,
            image: { type: "jpeg", quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
            jsPDF: { unit: "pt", format: "a4", orientation: "portrait" },
            pagebreak: { mode: ["avoid-all", "css", "legacy"] },
          })
          .from(templateEl)
          .save();
      } catch (error) {
        console.error("Error downloading intake form:", error);
        this.$store.commit("toast/NEW", {
          type: "error",
          message: error?.response?.data?.message || error?.message || "Couldn't download the intake form. Please try again.",
        });
      } finally {
        this.printFormData = null;
        this.printSubmittedAt = "";
        this.downloadingId = null;
        this.pendingDownloadAppointment = null;
      }
    },

    // ==================== EXPORT METHODS ====================

    exportAppointments() {
      const csvContent = this.convertToCSV(this.appointments);
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `appointments_page_${this.currentPage}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    },

    convertToCSV(appointments) {
      const headers = [
        "Patient Name",
        "Appointment ID",
        "Date",
        "Time",
        "Doctor",
        "Status",
      ];
      const rows = appointments.map((app) => [
        this.formatFullName(app.patient),
        app.appointmentCode || app.id,
        this.formatDate(app),
        this.formatTime(app),
        this.formatDoctorName(app.doctor),
        this.formatStatus(app.status),
      ]);

      return [headers, ...rows].map((row) => row.join(",")).join("\n");
    },

    // ==================== AUTO REFRESH ====================

    startAutoRefresh() {
      this.refreshInterval = setInterval(() => {
        if (!this.showEditModal) {
          this.getAppointments(true);
        }
      }, 30000);
    },

    stopAutoRefresh() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval);
        this.refreshInterval = null;
      }
    },
  },
  created() {
    this.getAppointments();
    this.getDoctors();
    this.fetchAnalytics();
    this.fetchAppointmentReasons();
    this.startAutoRefresh();
  },
  beforeUnmount() {
    this.stopAutoRefresh();
    if (this.flatpickrInstance) {
      this.flatpickrInstance.destroy();
    }
    if (this.applyFiltersDebounced && this.applyFiltersDebounced.cancel) {
      this.applyFiltersDebounced.cancel();
    }
  },
  watch: {
    "filters.search": {
      handler() {
        this.applyFiltersDebounced();
      },
      deep: false
    },
    "filters.doctor": {
      handler() {
        this.applyFiltersDebounced();
      },
      deep: false
    },
    "filters.status": {
      handler() {
        this.applyFiltersDebounced();
      },
      deep: false
    },
    "filters.timePeriod": {
      handler(newVal) {
        if (newVal === 'custom' && !this.filters.customStartDate && !this.filters.customEndDate) {
          return;
        }
        this.applyFiltersDebounced();
      },
      deep: false
    },
    "filters.customStartDate": {
      handler() {
        this.applyFiltersDebounced();
      },
      deep: false
    },
    "filters.customEndDate": {
      handler() {
        this.applyFiltersDebounced();
      },
      deep: false
    },
    "filters.entranceType": {
      handler() {
        this.applyFiltersDebounced();
      },
      deep: false
    },
    analyticsPeriod: {
      handler() {
        this.fetchAnalytics();
      },
      immediate: false
    },

    "editForm.doctorId"(newDoctorId) {
      if (!newDoctorId || this.modalLoading) return;

      this.selectedDoctorAvailability = this.doctorAvailability.find(
        (d) => d.id === newDoctorId
      );

      if (!this.modalLoading) {
        this.editForm.appointmentTypeCode = "";
        this.editForm.date = "";
        this.editForm.timeSlot = "";
        this.availableTimeSlots = [];
        this.availableTimeSlots24hr = [];
      }

      this.fetchDoctorAvailability().then(() => {
        this.initializeFlatpickr();
      });
    },

    "editForm.appointmentType"(newType) {
      if (!this.modalLoading && this.editForm.doctorId && newType) {
        this.editForm.date = "";
        this.editForm.timeSlot = "";
        this.availableTimeSlots = [];
        this.availableTimeSlots24hr = [];

        this.$nextTick(() => {
          this.initializeFlatpickr();
        });
      }
    },

    "editForm.date"(newDate) {
      this.availableTimeSlots = [];
      this.availableTimeSlots24hr = [];
      this.editForm.timeSlot = "";

      if (!newDate) return;

      this.buildTimeSlotsForDate(newDate);
    },
  },
};
</script>

<style scoped>
.intake-print-offscreen {
  /* "fixed" anchors to the viewport instead of the document, so — unlike
     "absolute" — it does not get included when the browser computes the
     page's scrollable height. That's what was causing the huge blank
     scroll area: this element sits outside .wrapper's own overflow:hidden
     box, so as "absolute" its ~2000px-tall content was inflating the
     whole document's scroll height even while positioned off-screen. */
  position: fixed;
  top: 0;
  left: -99999px;
  z-index: -1;
}

/* All your existing styles remain exactly the same */
.mng-appoits {
  height: 100vh !important;
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-start !important;
}

.manage-appointments-container {
  flex: 1;
  overflow-y: auto;
  padding: 40px 20px 100px;
  max-width: 1500px;
  /* margin: 00px auto 60px; */
  width: 100%;
}

.manage-appointments-container::-webkit-scrollbar {
  width: 20px;
}

.manage-appointments-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.manage-appointments-container::-webkit-scrollbar-thumb {
  background: #007bff4e;
  border-radius: 10px;
}

.page-header {
  margin-bottom: 30px;
  text-align: center;
}

.page-header h1 {
  color: #1c3476;
  margin: 0 0 10px 0;
  font-size: 44px;
  font-weight: 800;
}

.page-header p {
  color: #7f8c8d;
  margin: 0;
  font-size: 16px;
}

.filters-section,
.appointments-table-section {
  width: 100%;
  min-width: 0;
}

.filters-section {
  background: white;
  padding: 25px;
  border-radius: 10px;
  margin-bottom: 25px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.filter-row {
  display: flex;
  align-items: flex-end;
  gap: 15px;
  flex-wrap: nowrap;
  overflow-x: auto;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 180px;
  max-width: 240px;
  flex-shrink: 0;
}

.filter-actions {
  display: flex;
  gap: 12px;
  align-items: flex-end;
  flex-shrink: 0;
}

.filter-actions-right {
  margin-left: auto;
}


.filter-nav-btn {
  justify-content: center;
  min-height: 44px;
  white-space: nowrap;
}

.filter-group label {
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
  white-space: nowrap;
}

.filter-select,
.filter-input {
  padding: 10px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s;
  width: 100%;
}

.filter-select:focus,
.filter-input:focus {
  outline: none;
  border-color: #007bff;
}

.search-box {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  right: 12px;
  color: #aaa;
  font-size: 13px;
  pointer-events: none;
}

/* Two classes (not one) to out-specificity the plain ".filter-input" rule
   further down this file that would otherwise win on source order alone
   and reset this padding back to 12px, letting the icon sit on top of
   typed text. */
.filter-input.search-input {
  padding-right: 34px;
}

.stats-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 25px;
}

.stat-card {
  background: white;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.stat-number {
  font-size: 32px;
  font-weight: bold;
  color: #2c3e50;
  margin-bottom: 5px;
}

.stat-label {
  color: #7f8c8d;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.appointments-table-section {
  background: white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  border-bottom: 1px solid #eee;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.table-header h3 {
  margin: 0;
  color: #2c3e50;
}

.items-per-page {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #7f8c8d;
}

.items-select {
  padding: 6px 10px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  background: white;
  color: #2c3e50;
}

.items-select:focus {
  outline: none;
  border-color: #007bff;
}

.table-actions {
  display: flex;
  gap: 10px;
}

.queue-table-container {
  overflow-x: auto;
  width: 100%;
}

.pagination-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 25px;
  border-top: 1px solid #eee;
  background: #fafbfc;
}

.pagination-info {
  font-size: 13px;
  color: #7f8c8d;
}

.pagination-info p {
  margin: 0;
}

.pagination-info strong {
  color: #2c3e50;
  font-weight: 600;
}

.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
}

.pagination-btn {
  padding: 8px 14px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  background: white;
  color: #007bff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
}

.pagination-btn:hover:not(:disabled) {
  background: #007bff;
  color: white;
  border-color: #007bff;
}

.pagination-btn:disabled {
  color: #bbb;
  cursor: not-allowed;
  opacity: 0.5;
}

.pagination-numbers {
  display: flex;
  gap: 4px;
}

.pagination-number {
  width: 36px;
  height: 36px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  background: white;
  color: #2c3e50;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pagination-number:hover:not(.active) {
  border-color: #007bff;
  color: #007bff;
}

.pagination-number.active {
  background: #007bff;
  color: white;
  border-color: #007bff;
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.3);
}

.queue-table {
  width: 100%;
  border-collapse: collapse;
}

.queue-table th {
  background: #f8f9fa;
  padding: 15px 12px;
  text-align: left;
  font-weight: 600;
  color: #2c3e50;
  border-bottom: 2px solid #dee2e6;
  white-space: nowrap;
}

.queue-table td {
  padding: 15px 12px;
  border-bottom: 1px solid #eee;
  vertical-align: middle;
}

.patient-info {
  display: flex;
  flex-direction: column;
}

.patient-info small {
  color: #7f8c8d;
  font-size: 12px;
  margin-top: 2px;
}

.status-badge {
  padding: 6px 12px;
  border-radius: 5px;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-pending {
  background: #fff3cd;
  color: #856404;
}

.status-approved {
  background: #d1edff;
  color: #004085;
}

.status-cancelled {
  background: #f8d7da;
  color: #721c24;
}

.status-completed {
  background: #00a929;
  color: #fff;
}

.action-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0056b3;
}

.btn-primary:disabled {
  background: #6c9bcf;
  cursor: not-allowed;
  opacity: 0.7;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background: #545b62;
}

.btn-secondary:disabled {
  background: #999;
  cursor: not-allowed;
  opacity: 0.7;
}

.btn-outline {
  background: transparent;
  color: #007bff;
  border: 1px solid #007bff;
}

.btn-outline:hover {
  background: #007bff;
  color: white;
}

.btn-sm {
  padding: 10px 16px;
  font-size: 14px;
}

.no-appointments {
  text-align: center;
  padding: 60px 20px;
  color: #7f8c8d;
}

.no-data-icon {
  font-size: 48px;
  margin-bottom: 15px;
}

.no-appointments h4 {
  margin: 0 0 10px 0;
  color: #2c3e50;
}

/* Modal Styles */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeInModal 0.3s ease-out;
}

@keyframes fadeInModal {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.modal-content {
  background: white;
  padding: 30px;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  animation: slideInModal 0.3s ease-out;
  width: 100% !important;
}

@keyframes slideInModal {
  from {
    transform: translateY(-20px);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.edit-modal-content {
  width: 100%;
  max-width: 900px;
}

.modal-title {
  font-size: 24px;
  font-weight: 700;
  color: #1c3476;
  margin: 0 0 25px 0;
  padding-top: 10px;
}

.form-error-banner {
  color: #b3261e;
  font-size: 13px;
}

.close-btn {
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #7f8c8d;
  padding: 5px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #f0f0f0;
  color: #dc3545;
}

.modalDesign .modal-footer button {
  display: block;
}

.modal-body {
  margin: 0 0 20px 0;
}

.edit-form-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.patient-card {
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
  border: 1px solid #e0e6ed;
  padding: 20px;
  border-radius: 10px;
  margin-bottom: 5px;
}

.patient-card-header {
  display: flex;
  align-items: center;
  gap: 15px;
}

.patient-avatar {
  width: 50px;
  height: 50px;
  background: #007bff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
}

.patient-details {
  flex: 1;
  text-align: left;
}

.patient-name {
  margin: 0 0 4px 0;
  font-size: 18px;
  font-weight: 600;
  color: #1c3476;
}

.patient-id {
  margin: 0;
  font-size: 13px;
  color: #7f8c8d;
}

.patient-meta {
  display: grid;
  grid-template-columns: minmax(180px, 1.2fr) repeat(2, minmax(150px, 1fr));
  gap: 10px;
  margin-top: 12px;
}

.patient-meta-item {
  min-width: 0;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.74);
  border: 1px solid #dfe6ef;
  border-radius: 8px;
}

.patient-meta-item.appointment-code {
  background: #eef6ff;
  border-color: #bfdbfe;
}

.patient-meta-label {
  display: block;
  margin-bottom: 3px;
  color: #6b7280;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}

.patient-meta-value {
  display: block;
  color: #1f2937;
  font-size: 13px;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.form-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  background: #f9f9fb;
  border-radius: 10px;
  border: 1px solid #eef1f5;
}

.form-row {
  display: flex;
  gap: 16px;
}

.form-group {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group.statuss {
  flex: 0.8;
}

.form-group.doctorss {
  flex: 1.2;
}

.form-label {
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 4px;
}

.required {
  color: #dc3545;
  font-weight: 700;
}

.form-control {
  padding: 11px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 7px;
  font-size: 14px;
  transition: all 0.3s;
  background: white;
  color: #2c3e50;
}

.form-control:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.form-control:disabled {
  background: #f5f5f5;
  color: #999;
  cursor: not-allowed;
}

.form-select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23666' stroke-width='2' fill='none'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 12px 8px;
  padding-right: 35px;
  cursor: pointer;
}

.info-message-container {
  padding: 20px;
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.info-message {
  background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
  border: 1px solid #bbdefb;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.info-icon {
  font-size: 20px;
  flex-shrink: 0;
}

.info-text {
  flex: 1;
  text-align: left;
}

.info-title {
  margin: 0 0 4px 0;
  font-weight: 600;
  color: #1c3476;
  font-size: 14px;
}

.info-description {
  margin: 0;
  color: #7f8c8d;
  font-size: 13px;
  line-height: 1.5;
}

.calendar-timeslots-section {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 20px;
  background: #f9f9fb;
  border-radius: 10px;
  border: 1px solid #eef1f5;
  animation: slideDown 0.4s ease-out;
}

.calendar-wrapper {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.calendar-label {
  margin-bottom: 5px;
}

.flatpickr-wrapper {
  position: relative;
}

.flatpickr-input {
  width: 100%;
  padding: 12px;
  border: 1px solid #dcdfe6;
  border-radius: 7px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
  background: white;
  color: #2c3e50;
  font-weight: 500;
}

.flatpickr-input:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.timeslots-wrapper {
  display: flex;
  flex-direction: column;
  gap: 12px;
  animation: slideDown 0.4s ease-out;
}

.timeslots-container {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  padding: 2px;
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 8px;
  width: 100%;
}

.timeslots-container::-webkit-scrollbar {
  width: 20px;
}

.timeslots-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.timeslots-container::-webkit-scrollbar-thumb {
  background: #007bff;
  border-radius: 10px;
}

.timeslots-container::-webkit-scrollbar-thumb:hover {
  background: #0056b3;
}

.timeslot-pill {
  padding: 12px 16px;
  border: 2px solid #dcdfe6;
  border-radius: 20px;
  background: white;
  color: #2c3e50;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  text-align: center;
}

.timeslot-pill:hover {
  border-color: #007bff;
  background: #f0f8ff;
  color: #007bff;
}

.timeslot-pill.timeslot-active {
  background: #007bff;
  color: white;
  border-color: #007bff;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

.no-slots-message {
  padding: 12px;
  background: #fff3cd;
  color: #856404;
  border-radius: 6px;
  font-size: 13px;
  text-align: center;
}

.modal-footer {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.btn-update {
  min-width: 160px;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  display: inline-block;
}

.blue-spinner {
  border-top: 2px solid #007bff;
}

.timeslots-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px;
  gap: 12px;
  background: white;
  border-radius: 10px;
  border: 1px dashed #dcdfe6;
}

.timeslots-loader p {
  margin: 0;
  color: #7f8c8d;
  font-size: 14px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.loader {
  text-align: center;
  padding: 40px;
  font-size: 16px;
  color: #7f8c8d;
}

.modal-skeleton {
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.skeleton-patient-card {
  background: #f0f0f0;
  padding: 20px;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.skeleton-line {
  height: 14px;
  background: #e5e7eb;
  border-radius: 6px;
  animation: pulse 1.5s infinite ease-in-out;
}

.skeleton-input {
  height: 42px;
  background: #e5e7eb;
  border-radius: 8px;
  width: 100%;
  animation: pulse 1.5s infinite ease-in-out;
}

.skeleton-group {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.w-60 {
  width: 60%;
}

.w-40 {
  width: 40%;
}

.w-70 {
  width: 70%;
}

.w-80 {
  width: 80%;
}

@keyframes pulse {
  0% {
    opacity: 1;
  }

  50% {
    opacity: 0.4;
  }

  100% {
    opacity: 1;
  }
}

.expand-enter-active,
.expand-leave-active {
  transition: all 0.4s ease;
}

.expand-enter-from {
  opacity: 0;
  transform: translateY(-20px);
  max-height: 0;
}

.expand-leave-to {
  opacity: 0;
  transform: translateY(-20px);
  max-height: 0;
}

.expand-enter-to,
.expand-leave-from {
  opacity: 1;
  transform: translateY(0);
  max-height: 1000px;
}

@media (max-width: 600px) {
  .modal-content {
    width: 95%;
    padding: 20px;
    max-height: 95vh;
  }

  .form-row {
    flex-direction: column;
  }

  .filter-row {
    flex-direction: column;
    gap: 12px;
  }

  .filter-actions {
    flex-direction: column;
    margin-left: 0;
    gap: 10px;
  }

  .btn {
    width: 100%;
    justify-content: center;
  }

  .timeslots-container {
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
  }

  .timeslot-pill {
    padding: 10px 12px;
    font-size: 12px;
  }

  .patient-name {
    font-size: 16px;
  }

  .modal-title {
    font-size: 20px;
  }

  .header-left {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .pagination-controls {
    flex-direction: column;
  }

  .pagination-numbers {
    justify-content: center;
  }

  .pagination-btn {
    width: 100%;
  }

  .pagination-section {
    gap: 12px;
    padding: 15px;
  }
}

.mng-appoits {
  height: auto;
}

.manage-appointments-container {
  padding: 40px 10px 60px;
  max-width: 1500px;
  margin: 100px auto 0;
  width: 100%;
}

.page-header {
  margin-bottom: 30px;
  text-align: center;
}

.page-header h1 {
  color: #1c3476;
  margin: 0 0 10px 0;
  font-size: 44px;
  font-weight: 800;
}

.page-header p {
  color: #7f8c8d;
  margin: 0;
  font-size: 16px;
}

.filters-section,
.appointments-table-section {
  width: 100%;
  min-width: 0;
}

.filters-section {
  background: white;
  padding: 25px;
  border-radius: 10px;
  margin-bottom: 25px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.filter-row {
  display: flex;
  align-items: flex-end;
  gap: 15px;
  flex-wrap: nowrap;
  overflow-x: auto;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 180px;
  max-width: 240px;
  flex-shrink: 0;
}

.filter-actions {
  display: flex;
  gap: 12px;
  align-items: flex-end;
  flex-shrink: 0;
}

.filter-actions-right {
  margin-left: auto;
}

.filter-nav-btn {
  justify-content: center;
  min-height: 44px;
  white-space: nowrap;
}

.filter-group label {
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
  white-space: nowrap;
}

.filter-select,
.filter-input {
  padding: 10px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s;
  width: 100%;
}

.filter-select:focus,
.filter-input:focus {
  outline: none;
  border-color: #007bff;
}

/* .filter-actions {
  display: flex;
  gap: 12px;
  align-items: end;
  flex-shrink: 0;
  margin-left: auto;
} */

.stats-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 25px;
}

.stat-card {
  background: white;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.stat-number {
  font-size: 32px;
  font-weight: bold;
  color: #2c3e50;
  margin-bottom: 5px;
}

.stat-label {
  color: #7f8c8d;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.analytics-header {
  margin: 30px 0 20px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  padding: 15px 25px;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.analytics-title h3 {
  margin: 0;
  font-size: 20px;
  color: #1c3476;
  font-weight: 700;
}

.analytics-title p {
  margin: 4px 0 0 0;
  font-size: 13px;
  color: #7f8c8d;
}

/* Period Toggle */
.period-toggle {
  display: flex;
  background: #f1f3f5;
  padding: 4px;
  border-radius: 8px;
}

.toggle-btn {
  padding: 8px 20px;
  border: none;
  background: transparent;
  font-size: 14px;
  font-weight: 600;
  color: #7f8c8d;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.toggle-btn.active {
  background: white;
  color: #1c3476;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

/* Premium Analytics Cards */
.analytics-section {
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
  margin-bottom: 40px;
}

.premium-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  padding: 24px;
  overflow: hidden;
  border-bottom: none !important;
  background: white;
  border-radius: 16px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
}

.premium-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.card-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  margin-bottom: 16px;
}

.card-content {
  z-index: 1;
  width: 100%;
}

.stat-main {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 4px;
}

.stat-number {
  font-size: 28px;
  margin: 0;
}

.stat-diff {
  font-size: 13px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 20px;
}

.up {
  color: #dc3545;
  background: #fff5f5;
}

.down {
  color: #28a745;
  background: #f6ffed;
}

.up-good {
  color: #28a745;
  background: #f6ffed;
}

.down-bad {
  color: #dc3545;
  background: #fff5f5;
}

.sparkline-wrapper {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 50px;
  opacity: 0.6;
}

.sparkline {
  width: 100%;
  height: 100%;
}

/* Color Themes */
.card-failed .card-icon {
  background: #fee2e2;
  color: #dc3545;
}

.card-failed .sparkline {
  color: #dc3545;
}

.card-missed .card-icon {
  background: #ffedd5;
  color: #fd7e14;
}

.card-missed .sparkline {
  color: #fd7e14;
}

.card-successful .card-icon {
  background: #dcfce7;
  color: #28a745;
}

.card-successful .sparkline {
  color: #28a745;
}

.card-registered .card-icon {
  background: #dbeafe;
  color: #007bff;
}

.card-registered .sparkline {
  color: #007bff;
}

.card-ratio .card-icon {
  background: #f3e8ff;
  color: #6f42c1;
}

.card-ratio .sparkline {
  color: #6f42c1;
}

.appointments-table-section {
  background: white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  border-bottom: 1px solid #eee;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.table-header h3 {
  margin: 0;
  color: #2c3e50;
}

.items-per-page {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #7f8c8d;
}

.items-select {
  padding: 6px 10px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  background: white;
  color: #2c3e50;
}

.items-select:focus {
  outline: none;
  border-color: #007bff;
}

.table-actions {
  display: flex;
  gap: 10px;
}

.queue-table-container {
  overflow-x: auto;
  width: 100%;
}

/* Pagination Styles */
.pagination-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 25px;
  border-top: 1px solid #eee;
  background: #fafbfc;
}

.pagination-info {
  font-size: 13px;
  color: #7f8c8d;
}

.pagination-info p {
  margin: 0;
}

.pagination-info strong {
  color: #2c3e50;
  font-weight: 600;
}

.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
}

.pagination-btn {
  padding: 8px 14px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  background: white;
  color: #007bff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
}

.pagination-btn:hover:not(:disabled) {
  background: #007bff;
  color: white;
  border-color: #007bff;
}

.pagination-btn:disabled {
  color: #bbb;
  cursor: not-allowed;
  opacity: 0.5;
}

.pagination-numbers {
  display: flex;
  gap: 4px;
}

.pagination-number {
  width: 36px;
  height: 36px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  background: white;
  color: #2c3e50;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pagination-number:hover:not(.active) {
  border-color: #007bff;
  color: #007bff;
}

.pagination-number.active {
  background: #007bff;
  color: white;
  border-color: #007bff;
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.3);
}

.queue-table {
  width: 100%;
  border-collapse: collapse;
}

.queue-table th {
  background: #f8f9fa;
  padding: 15px 12px;
  text-align: left;
  font-weight: 600;
  color: #2c3e50;
  border-bottom: 2px solid #dee2e6;
  white-space: nowrap;
}

.queue-table td {
  padding: 15px 12px;
  border-bottom: 1px solid #eee;
  vertical-align: middle;
}

.patient-info {
  display: flex;
  flex-direction: column;
}

.patient-info small {
  color: #7f8c8d;
  font-size: 12px;
  margin-top: 2px;
}

.status-badge {
  padding: 6px 12px;
  border-radius: 5px;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-pending {
  background: #fff3cd;
  color: #856404;
}

.status-approved {
  background: #d1edff;
  color: #004085;
}

.status-cancelled {
  background: #f8d7da;
  color: #721c24;
}

.status-completed {
  background: #00a929;
  color: #fff;
}

.action-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0056b3;
}

.btn-primary:disabled {
  background: #6c9bcf;
  cursor: not-allowed;
  opacity: 0.7;
}

.btn-success {
  background: #28a745;
  color: white;
}

.btn-success:hover {
  background: #1e7e34;
}

.btn-danger {
  background: #dc3545;
  color: white;
}

.btn-danger:hover {
  background: #c82333;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background: #545b62;
}

.btn-secondary:disabled {
  background: #999;
  cursor: not-allowed;
  opacity: 0.7;
}

.btn-outline {
  background: transparent;
  color: #007bff;
  border: 1px solid #007bff;
}

.btn-outline:hover {
  background: #007bff;
  color: white;
}

.btn-sm {
  padding: 10px 16px;
  font-size: 14px;
}

.no-appointments {
  text-align: center;
  padding: 60px 20px;
  color: #7f8c8d;
}

.no-data-icon {
  font-size: 48px;
  margin-bottom: 15px;
}

.no-appointments h4 {
  margin: 0 0 10px 0;
  color: #2c3e50;
}

/* Modal Styles */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeInModal 0.3s ease-out;
}

@keyframes fadeInModal {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.modal-content {
  background: white;
  padding: 30px;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  animation: slideInModal 0.3s ease-out;
  width: 100% !important;
}

@keyframes slideInModal {
  from {
    transform: translateY(-20px);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.edit-modal-content {
  width: 100%;
  max-width: 900px;
}

.modal-title {
  font-size: 24px;
  font-weight: 700;
  color: #1c3476;
  margin: 0 0 25px 0;
  padding-top: 10px;
}

.form-error-banner {
  color: #b3261e;
  font-size: 13px;
}

.close-btn {
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #7f8c8d;
  padding: 5px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #f0f0f0;
  color: #dc3545;
}

.modalDesign .modal-footer button {
  display: block;
}

.modal-body {
  margin: 0 0 20px 0;
}

.edit-form-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Patient Card - Enhanced Styling */
.patient-card {
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
  border: 1px solid #e0e6ed;
  padding: 20px;
  border-radius: 10px;
  margin-bottom: 5px;
}

.patient-card-header {
  display: flex;
  align-items: center;
  gap: 15px;
}

.patient-avatar {
  width: 50px;
  height: 50px;
  background: #007bff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
}

.patient-details {
  flex: 1;
  text-align: left;
}

.patient-name {
  margin: 0 0 4px 0;
  font-size: 18px;
  font-weight: 600;
  color: #1c3476;
}

.patient-id {
  margin: 0;
  font-size: 13px;
  color: #7f8c8d;
}

.patient-meta {
  display: grid;
  grid-template-columns: minmax(180px, 1.2fr) repeat(2, minmax(150px, 1fr));
  gap: 10px;
  margin-top: 12px;
}

.patient-meta-item {
  min-width: 0;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.74);
  border: 1px solid #dfe6ef;
  border-radius: 8px;
}

.patient-meta-item.appointment-code {
  background: #eef6ff;
  border-color: #bfdbfe;
}

.patient-meta-label {
  display: block;
  margin-bottom: 3px;
  color: #6b7280;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}

.patient-meta-value {
  display: block;
  color: #1f2937;
  font-size: 13px;
  font-weight: 600;
  overflow-wrap: anywhere;
}

/* Form Section */
.form-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  background: #f9f9fb;
  border-radius: 10px;
  border: 1px solid #eef1f5;
}

.form-row {
  display: flex;
  gap: 16px;
}

.form-row:last-child {
  margin-bottom: 0;
}

.form-group {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group.statuss {
  flex: 0.8;
}

.form-group.doctorss {
  flex: 1.2;
}

.form-label {
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 4px;
}

.required {
  color: #dc3545;
  font-weight: 700;
}

.form-control {
  padding: 11px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 7px;
  font-size: 14px;
  transition: all 0.3s;
  background: white;
  color: #2c3e50;
}

.form-control:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.form-control:disabled {
  background: #f5f5f5;
  color: #999;
  cursor: not-allowed;
}

.form-select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23666' stroke-width='2' fill='none'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 12px 8px;
  padding-right: 35px;
  cursor: pointer;
}

/* Info Message */
.info-message-container {
  padding: 20px;
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.info-message {
  background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
  border: 1px solid #bbdefb;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.info-icon {
  font-size: 20px;
  flex-shrink: 0;
}

.info-text {
  flex: 1;
  text-align: left;
}

.info-title {
  margin: 0 0 4px 0;
  font-weight: 600;
  color: #1c3476;
  font-size: 14px;
}

.info-description {
  margin: 0;
  color: #7f8c8d;
  font-size: 13px;
  line-height: 1.5;
}

/* Calendar and Time Slots Section */
.calendar-timeslots-section {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 20px;
  background: #f9f9fb;
  border-radius: 10px;
  border: 1px solid #eef1f5;
  animation: slideDown 0.4s ease-out;
}

.calendar-wrapper {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.calendar-label {
  margin-bottom: 5px;
}

.flatpickr-wrapper {
  position: relative;
}

.flatpickr-input {
  width: 100%;
  padding: 12px;
  border: 1px solid #dcdfe6;
  border-radius: 7px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
  background: white;
  color: #2c3e50;
  font-weight: 500;
}

.flatpickr-input:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

/* Flatpickr Custom Styling */
:deep(.flatpickr-calendar) {
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border: 1px solid #e0e6ed;
}

:deep(.flatpickr-monthDropdown-months),
:deep(.flatpickr-monthDropdown-months) {
  background: #f5f7fa;
  color: #1c3476;
  border-radius: 4px;
  border: 1px solid #dcdfe6;
}

:deep(.flatpickr-current-month) {
  color: #1c3476;
  font-weight: 600;
}

:deep(.flatpickr-day.selected) {
  background: #007bff;
  border-color: #007bff;
  color: white;
  font-weight: 600;
}

:deep(.flatpickr-day.selected:hover) {
  background: #0056b3;
  border-color: #0056b3;
}

:deep(.flatpickr-day.today) {
  border-color: #007bff;
  color: #007bff;
  font-weight: 600;
}

:deep(.flatpickr-day:hover) {
  background: #f0f4f8;
  color: #1c3476;
}

:deep(.flatpickr-day.inRange) {
  background: #d1edff;
  box-shadow: -5px 0 0 #d1edff, 5px 0 0 #d1edff;
  color: #004085;
}

:deep(.flatpickr-weekdays) {
  background: #f8f9fa;
  color: #2c3e50;
  font-weight: 600;
  padding: 8px 0;
}

/* Time Slots Section */
.timeslots-wrapper {
  display: flex;
  flex-direction: column;
  gap: 12px;
  animation: slideDown 0.4s ease-out;
}

.timeslots-container {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  padding: 2px;
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 8px;
  width: 100%;
}

.timeslots-container::-webkit-scrollbar {
  width: 6px;
}

.timeslots-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.timeslots-container::-webkit-scrollbar-thumb {
  background: #007bff;
  border-radius: 10px;
}

.timeslots-container::-webkit-scrollbar-thumb:hover {
  background: #0056b3;
}

.timeslot-pill {
  padding: 12px 16px;
  border: 2px solid #dcdfe6;
  border-radius: 20px;
  background: white;
  color: #2c3e50;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  text-align: center;
}

.timeslot-pill:hover {
  border-color: #007bff;
  background: #f0f8ff;
  color: #007bff;
}

.timeslot-pill.timeslot-active {
  background: #007bff;
  color: white;
  border-color: #007bff;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

.no-slots-message {
  padding: 12px;
  background: #fff3cd;
  color: #856404;
  border-radius: 6px;
  font-size: 13px;
  text-align: center;
}

/* Modal Footer */
.modal-footer {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.btn-update {
  min-width: 160px;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  display: inline-block;
}

.blue-spinner {
  border-top: 2px solid #007bff;
}

.timeslots-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px;
  gap: 12px;
  background: white;
  border-radius: 10px;
  border: 1px dashed #dcdfe6;
}

.timeslots-loader p {
  margin: 0;
  color: #7f8c8d;
  font-size: 14px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.loader {
  text-align: center;
  padding: 40px;
  font-size: 16px;
  color: #7f8c8d;
}

.appoint-approval-main {
  display: block;
  padding-top: 120px;
}

.mng-appoits {
  min-height: 100vh;
  height: auto !important;
  padding-top: 60px;
}

.mng-appoits :deep(.bck-btns) {
  z-index: 10;
}

.manage-appointments-container {
  padding-top: 12px;
}

/* Skeleton Loading */
.modal-skeleton {
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.skeleton-patient-card {
  background: #f0f0f0;
  padding: 20px;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.skeleton-line {
  height: 14px;
  background: #e5e7eb;
  border-radius: 6px;
  animation: pulse 1.5s infinite ease-in-out;
}

.skeleton-input {
  height: 42px;
  background: #e5e7eb;
  border-radius: 8px;
  width: 100%;
  animation: pulse 1.5s infinite ease-in-out;
}

.skeleton-group {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.w-60 {
  width: 60%;
}

.w-40 {
  width: 40%;
}

.w-70 {
  width: 70%;
}

.w-80 {
  width: 80%;
}

@keyframes pulse {
  0% {
    opacity: 1;
  }

  50% {
    opacity: 0.4;
  }

  100% {
    opacity: 1;
  }
}

/* Transitions */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.4s ease;
}

.expand-enter-from {
  opacity: 0;
  transform: translateY(-20px);
  max-height: 0;
}

.expand-leave-to {
  opacity: 0;
  transform: translateY(-20px);
  max-height: 0;
}

.expand-enter-to,
.expand-leave-from {
  opacity: 1;
  transform: translateY(0);
  max-height: 1000px;
}

/* Responsive */
@media (max-width: 600px) {
  .mng-appoits {
    padding-top: 135px;
  }

  .manage-appointments-container {
    margin: 0 auto;
    padding-top: 8px;
  }

  .modal-content {
    width: 95%;
    padding: 20px;
    max-height: 95vh;
  }

  .form-row {
    flex-direction: column;
  }

  .filter-row {
    flex-direction: column;
    gap: 12px;
  }

  .filter-actions {
    flex-direction: column;
    margin-left: 0;
    gap: 10px;
  }

  .btn {
    width: 100%;
    justify-content: center;
  }

  .timeslots-container {
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
  }

  .timeslot-pill {
    padding: 10px 12px;
    font-size: 12px;
  }

  .patient-name {
    font-size: 16px;
  }

  .modal-title {
    font-size: 20px;
  }

  /* Pagination responsive */
  .header-left {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .pagination-controls {
    flex-direction: column;
  }

  .pagination-numbers {
    justify-content: center;
  }

  .pagination-btn {
    width: 100%;
  }

  .pagination-section {
    gap: 12px;
    padding: 15px;
  }
}
/* Responsive filters — 2-column grid on tablets and mobile */
@media (max-width: 1024px) {
  .filter-row {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    gap: 12px !important;
    flex-wrap: unset !important;
    overflow-x: unset !important;
    align-items: end;
  }

  .filter-group {
    min-width: 0 !important;
    max-width: none !important;
    flex-shrink: unset !important;
  }

  .filter-actions {
    grid-column: 1 / -1;
    flex-direction: row !important;
    flex-shrink: unset !important;
    margin-left: 0 !important;
  }

  .filter-nav-btn,
  .filter-actions .btn {
    flex: 1;
    justify-content: center;
  }
}
</style>
